import { t } from '../i18n';
import type { BinaryChannel } from '../network/BinaryChannel';
import { NetworkHub } from '../network/NetworkHub';
import type { OnlineIdentity } from '../online/OnlineIdentity';
import type { ChatModel } from './ChatModel';
import type { ChatPanel } from './ChatPanel';
import {
  decodeChatServerEnvelope,
  encodeChatClientEnvelope,
  type ChatClientEnvelope,
  type ChatServerEnvelope,
} from './ChatProtocol';
import type { ChatSettings } from './ChatTypes';

export interface GlobalChatSession {
  readonly token: string;
  readonly identity: OnlineIdentity;
  readonly expiresAt: number;
}

export interface GlobalChatClientLabels {
  readonly connecting: string;
  readonly guestDisabled: string;
  readonly disabled: string;
}

export interface GlobalChatClientOptions {
  readonly url: string;
  readonly getSession: () => Promise<GlobalChatSession>;
  readonly model: ChatModel;
  readonly panel: Pick<ChatPanel, 'setSendEnabled' | 'setVisible'>;
  readonly labels?: Partial<GlobalChatClientLabels>;
}

interface PendingSend {
  readonly clientMessageId: string;
  readonly text: string;
  readonly resolve: () => void;
  readonly reject: (error: Error) => void;
}

const AUTH_TIMEOUT_MS = 5_000;
const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 8_000;

const defaultLabels = (): GlobalChatClientLabels => ({
  connecting: t('chat:global.connecting'),
  guestDisabled: t('chat:global.guestDisabled'),
  disabled: t('chat:global.disabled'),
});

/** Browser transport for the global chat channel. ChatModel and ChatPanel remain transport-agnostic. */
export class GlobalChatClient {
  readonly #url: string;
  readonly #getSession: () => Promise<GlobalChatSession>;
  readonly #model: ChatModel;
  readonly #panel: Pick<ChatPanel, 'setSendEnabled' | 'setVisible'>;
  readonly #labels: GlobalChatClientLabels;
  readonly #network = new NetworkHub();
  #channel: BinaryChannel | null = null;
  #unsubscribe: (() => void) | null = null;
  #authTimer: ReturnType<typeof setTimeout> | null = null;
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  #settings: ChatSettings | null = null;
  #identityKind: GlobalChatSession['identity']['kind'] | null = null;
  #pending: PendingSend | null = null;
  #generation = 0;
  #reconnectAttempt = 0;
  #started = false;
  #ready = false;
  #disposed = false;
  #startPromise: Promise<void> | null = null;
  #resolveStart: (() => void) | null = null;
  #rejectStart: ((error: Error) => void) | null = null;

  constructor(options: GlobalChatClientOptions) {
    if (!options.url) throw new Error('Global chat URL is required.');
    if (options.model.channelId !== 'global') throw new Error('GlobalChatClient requires a global ChatModel.');
    this.#url = options.url;
    this.#getSession = options.getSession;
    this.#model = options.model;
    this.#panel = options.panel;
    this.#labels = { ...defaultLabels(), ...options.labels };
    this.#panel.setVisible(false);
    this.#panel.setSendEnabled(false, this.#labels.connecting);
  }

  start(): Promise<void> {
    if (this.#disposed) return Promise.reject(new Error('Global chat client is disposed.'));
    if (this.#started) return this.#startPromise!;
    this.#started = true;
    this.#startPromise = new Promise<void>((resolve, reject) => {
      this.#resolveStart = resolve;
      this.#rejectStart = reject;
    });
    void this.#connect();
    return this.#startPromise;
  }

  send(text: string): Promise<void> {
    if (this.#disposed) return Promise.reject(new Error('Global chat client is disposed.'));
    if (!this.#started || !this.#ready || !this.#channel) return Promise.reject(new Error('Global chat is not connected.'));
    if (!this.#canSend()) return Promise.reject(new Error('Global chat sending is disabled.'));
    if (this.#pending) return Promise.reject(new Error('A global chat message is already awaiting acknowledgement.'));
    const clientMessageId = createClientMessageId();
    return new Promise<void>((resolve, reject) => {
      this.#pending = { clientMessageId, text, resolve, reject };
      if (!this.#sendPending()) {
        this.#pending = null;
        reject(new Error('Global chat transport is busy.'));
      }
    });
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#ready = false;
    this.#clearAuthTimer();
    if (this.#reconnectTimer !== null) clearTimeout(this.#reconnectTimer);
    this.#reconnectTimer = null;
    this.#unsubscribe?.();
    this.#unsubscribe = null;
    this.#channel?.dispose();
    this.#channel = null;
    this.#network.dispose();
    const disposed = new Error('Global chat client is disposed.');
    this.#pending?.reject(disposed);
    this.#pending = null;
    this.#rejectStart?.(disposed);
    this.#resolveStart = null;
    this.#rejectStart = null;
    this.#panel.setSendEnabled(false, this.#labels.connecting);
  }

  async #connect(): Promise<void> {
    if (this.#disposed || this.#channel) return;
    const generation = ++this.#generation;
    const channel = this.#network.openWebSocket(this.#url, {
      connectTimeoutMs: AUTH_TIMEOUT_MS,
      reconnect: false,
      maxBufferedBytes: 64 * 1024,
    });
    this.#channel = channel;
    this.#unsubscribe = channel.subscribe({
      onOpen: () => { void this.#authenticate(generation); },
      onMessage: (payload) => this.#onMessage(generation, payload),
      onClose: () => this.#onClose(generation),
    });
    try { await channel.connect(); }
    catch { this.#restart(generation); }
  }

  async #authenticate(generation: number): Promise<void> {
    let session: GlobalChatSession;
    try { session = await this.#getSession(); }
    catch { this.#restart(generation); return; }
    if (!this.#current(generation) || session.expiresAt <= Date.now() || !session.token) {
      this.#restart(generation);
      return;
    }
    this.#identityKind = session.identity.kind;
    const sent = this.#channel!.send(encodeChatClientEnvelope({ type: 'auth', token: session.token }));
    if (!sent) { this.#restart(generation); return; }
    this.#clearAuthTimer();
    this.#authTimer = setTimeout(() => this.#restart(generation), AUTH_TIMEOUT_MS);
  }

  #onMessage(generation: number, payload: ArrayBuffer): void {
    if (!this.#current(generation)) return;
    let envelope: ChatServerEnvelope;
    try { envelope = decodeChatServerEnvelope(payload); }
    catch { this.#restart(generation); return; }

    if (envelope.type === 'ready') {
      if (envelope.channelId !== this.#model.channelId) { this.#restart(generation); return; }
      this.#clearAuthTimer();
      this.#ready = true;
      this.#reconnectAttempt = 0;
      this.#settings = envelope.settings;
      // Ready begins an authoritative history replay; server sequences reset on restart.
      this.#model.clear();
      this.#model.receiveHistory(envelope.history);
      this.#applySettings();
      this.#resolveStart?.();
      this.#resolveStart = null;
      this.#rejectStart = null;
      if (this.#pending && !this.#sendPending()) this.#restart(generation);
      return;
    }
    if (!this.#ready) { this.#restart(generation); return; }

    switch (envelope.type) {
      case 'history':
        this.#model.receiveHistory(envelope.messages);
        break;
      case 'message':
        this.#model.receive(envelope.message);
        break;
      case 'settings':
        this.#settings = envelope.settings;
        this.#applySettings();
        break;
      case 'ack':
        if (this.#pending?.clientMessageId === envelope.clientMessageId) {
          const pending = this.#pending;
          this.#pending = null;
          pending.resolve();
        }
        break;
      case 'error':
        if (envelope.clientMessageId && this.#pending?.clientMessageId === envelope.clientMessageId) {
          const pending = this.#pending;
          this.#pending = null;
          pending.reject(new Error(envelope.message));
        }
        break;
    }
  }

  #onClose(generation: number): void {
    if (!this.#current(generation)) return;
    this.#dropConnection();
    this.#scheduleReconnect();
  }

  #restart(generation: number): void {
    if (!this.#current(generation)) return;
    this.#dropConnection();
    this.#scheduleReconnect();
  }

  #dropConnection(): void {
    this.#ready = false;
    this.#clearAuthTimer();
    this.#unsubscribe?.();
    this.#unsubscribe = null;
    this.#channel?.dispose();
    this.#channel = null;
    if (this.#settings?.globalEnabled) {
      this.#panel.setVisible(true);
      this.#panel.setSendEnabled(false, this.#labels.connecting);
    }
  }

  #scheduleReconnect(): void {
    if (this.#disposed || this.#reconnectTimer !== null) return;
    const exponent = Math.min(this.#reconnectAttempt, 4);
    const delay = Math.min(RECONNECT_BASE_MS * (2 ** exponent), RECONNECT_MAX_MS);
    this.#reconnectAttempt++;
    this.#reconnectTimer = setTimeout(() => {
      this.#reconnectTimer = null;
      void this.#connect();
    }, delay);
  }

  #applySettings(): void {
    const settings = this.#settings;
    if (!settings) return;
    this.#panel.setVisible(settings.globalEnabled);
    if (!settings.globalEnabled) {
      this.#panel.setSendEnabled(false, this.#labels.disabled);
      return;
    }
    if (this.#identityKind === 'guest' && !settings.guestGlobalEnabled) {
      this.#panel.setSendEnabled(false, this.#labels.guestDisabled);
      return;
    }
    this.#panel.setSendEnabled(this.#ready, this.#ready ? '' : this.#labels.connecting);
  }

  #canSend(): boolean {
    return Boolean(this.#settings?.globalEnabled
      && (this.#identityKind !== 'guest' || this.#settings.guestGlobalEnabled));
  }

  #sendPending(): boolean {
    if (!this.#pending || !this.#channel || !this.#ready) return false;
    const envelope: ChatClientEnvelope = {
      type: 'send', clientMessageId: this.#pending.clientMessageId, text: this.#pending.text,
    };
    return this.#channel.send(encodeChatClientEnvelope(envelope));
  }

  #current(generation: number): boolean {
    return !this.#disposed && this.#generation === generation && this.#channel !== null;
  }

  #clearAuthTimer(): void {
    if (this.#authTimer !== null) clearTimeout(this.#authTimer);
    this.#authTimer = null;
  }
}

let fallbackMessageSequence = 0;

function createClientMessageId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  fallbackMessageSequence = (fallbackMessageSequence + 1) % Number.MAX_SAFE_INTEGER;
  return `client-${Date.now()}-${fallbackMessageSequence}`;
}
